import type { APIMessage } from '@alternativamais/chat-core';
import { lazy, type ReactNode, Suspense } from 'react';
import { create } from 'zustand';

import type { Posicao } from '../componentes/Flutuante';
import type { SecaoUsuario } from '../telas/config/usuario/ConfigUsuario';

/**
 * Camadas por cima do app (perfil, visualizador, modais): abertas de
 * qualquer lugar (uma mensagem, a lista de membros, um menu) sem passar
 * por cada componente no caminho. Ficam empilhadas; Esc fecha a de cima.
 */
export type Camada =
  | { tipo: 'perfil'; userId: string; guildId: string | null; ancora: Posicao }
  | { tipo: 'imagem'; mensagem: APIMessage; anexoId: string }
  | { tipo: 'reacoes'; mensagem: APIMessage }
  | { tipo: 'encaminhar'; mensagem: APIMessage }
  | { tipo: 'criar-enquete'; canalId: string }
  | { tipo: 'status-personalizado' }
  | { tipo: 'nova-conversa' }
  | { tipo: 'config-grupo'; canalId: string }
  | { tipo: 'sair-grupo'; canalId: string }
  | { tipo: 'eventos'; guildId: string }
  | { tipo: 'evento'; guildId: string; eventoId: string }
  | { tipo: 'criar-evento'; guildId: string; eventoId?: string }
  | { tipo: 'config-thread'; threadId: string }
  | { tipo: 'pedir-notificacoes' }
  | { tipo: 'troca-rapida' }
  | { tipo: 'atalhos' }
  | { tipo: 'config-usuario'; secao?: SecaoUsuario }
  | { tipo: 'config-servidor'; guildId: string; secao?: string; alvo?: string }
  | {
      tipo: 'config-canal';
      canalId: string;
      secao?: 'visao-geral' | 'permissoes' | 'convites';
    }
  | { tipo: 'criar-canal'; guildId: string; categoriaId?: string }
  | { tipo: 'criar-categoria'; guildId: string }
  | { tipo: 'convidar'; guildId: string; canalId?: string }
  | {
      tipo: 'moderar';
      acao: 'castigar' | 'expulsar' | 'banir';
      guildId: string;
      userId: string;
    }
  | { tipo: 'criar-servidor' }
  | { tipo: 'entrar-servidor'; codigo?: string }
  | { tipo: 'boas-vindas'; guildId: string; regrasDepois?: boolean }
  | { tipo: 'regras'; guildId: string }
  | { tipo: 'sair-servidor'; guildId: string }
  | { tipo: 'notificacoes-servidor'; guildId: string };

type Estado = { pilha: Array<Camada & { chave: number }> };

const useCamadas = create<Estado>(() => ({ pilha: [] }));
let proxima = 1;

export function abrirCamada(camada: Camada) {
  useCamadas.setState((estado) => ({
    // Um perfil por vez (abrir outro troca); o resto empilha.
    pilha: [
      ...estado.pilha.filter(
        (item) =>
          item.tipo !== camada.tipo ||
          ![
            'perfil',
            'troca-rapida',
            'atalhos',
            'config-usuario',
            'config-servidor',
            'config-canal',
            'boas-vindas',
            'regras',
          ].includes(camada.tipo),
      ),
      { ...camada, chave: proxima++ },
    ],
  }));
}

export function fecharCamada(chave?: number) {
  useCamadas.setState((estado) => ({
    pilha:
      chave === undefined
        ? estado.pilha.slice(0, -1)
        : estado.pilha.filter((item) => item.chave !== chave),
  }));
}

/** Ficam abertas ao chegar no servidor delas (abertas antes de ir até ele). */
const DO_SERVIDOR_PARA_ONDE_SE_VAI = new Set([
  'convidar',
  'boas-vindas',
  'regras',
]);

/**
 * Fecha tudo; fica o que é do servidor para onde se vai: o convite (criar um
 * servidor leva até ele e já abre o convite), as boas-vindas e as regras
 * (entrar pelo convite).
 */
export function fecharTodasAsCamadas(servidor?: string | null) {
  useCamadas.setState((estado) => ({
    pilha: estado.pilha.filter(
      (item) =>
        DO_SERVIDOR_PARA_ONDE_SE_VAI.has(item.tipo) &&
        'guildId' in item &&
        item.guildId === servidor,
    ),
  }));
}

const PopoverPerfil = lazy(() => import('../telas/chat/perfil/PopoverPerfil'));
const Visualizador = lazy(() => import('../telas/chat/camadas/Visualizador'));
const QuemReagiu = lazy(() => import('../telas/chat/camadas/QuemReagiu'));
const Encaminhar = lazy(() => import('../telas/chat/camadas/Encaminhar'));
const CriarEnquete = lazy(() => import('../telas/chat/enquete/CriarEnquete'));
const StatusPersonalizado = lazy(
  () => import('../telas/chat/perfil/StatusPersonalizado'),
);
const NovaConversa = lazy(() => import('../telas/chat/dms/NovaConversa'));
const ConfigGrupo = lazy(() => import('../telas/chat/dms/ConfigGrupo'));
const SairGrupo = lazy(() => import('../telas/chat/dms/SairGrupo'));
const Eventos = lazy(() => import('../telas/chat/eventos/Eventos'));
const DetalheEvento = lazy(() => import('../telas/chat/eventos/DetalheEvento'));
const CriarEvento = lazy(() => import('../telas/chat/eventos/CriarEvento'));
const ConfigThread = lazy(() => import('../telas/chat/threads/ConfigThread'));
const TrocaRapida = lazy(() => import('../telas/chat/camadas/TrocaRapida'));
const ListaAtalhos = lazy(() => import('../telas/chat/camadas/ListaAtalhos'));
const ConfigUsuario = lazy(
  () => import('../telas/config/usuario/ConfigUsuario'),
);
const ConfigServidor = lazy(
  () => import('../telas/config/servidor/ConfigServidor'),
);
const ConfigCanal = lazy(() => import('../telas/config/servidor/ConfigCanal'));
const CriarCanal = lazy(() =>
  import('../telas/config/servidor/Canais').then((m) => ({
    default: m.CriarCanal,
  })),
);
const CriarCategoria = lazy(() =>
  import('../telas/config/servidor/Canais').then((m) => ({
    default: m.CriarCategoria,
  })),
);
const Convidar = lazy(() => import('../telas/config/servidor/Convidar'));
const Moderar = lazy(() => import('../telas/config/servidor/Moderar'));
const CriarServidor = lazy(() => import('../telas/servidores/CriarServidor'));
const EntrarServidor = lazy(() => import('../telas/servidores/EntrarServidor'));
const SairServidor = lazy(() => import('../telas/servidores/SairServidor'));
const TelaDeBoasVindas = lazy(
  () => import('../telas/servidores/TelaDeBoasVindas'),
);
const AceitarRegras = lazy(() => import('../telas/servidores/AceitarRegras'));
const NotificacoesServidor = lazy(
  () => import('../telas/config/servidor/NotificacoesServidor'),
);
const PedirNotificacoes = lazy(
  () => import('../telas/chat/notificacoes/PedirNotificacoes'),
);

function desenhar(camada: Camada, fechar: () => void): ReactNode {
  switch (camada.tipo) {
    case 'perfil':
      return <PopoverPerfil {...camada} aoFechar={fechar} />;
    case 'imagem':
      return <Visualizador {...camada} aoFechar={fechar} />;
    case 'reacoes':
      return <QuemReagiu mensagem={camada.mensagem} aoFechar={fechar} />;
    case 'encaminhar':
      return <Encaminhar mensagem={camada.mensagem} aoFechar={fechar} />;
    case 'criar-enquete':
      return <CriarEnquete canalId={camada.canalId} aoFechar={fechar} />;
    case 'status-personalizado':
      return <StatusPersonalizado aoFechar={fechar} />;
    case 'nova-conversa':
      return <NovaConversa aoFechar={fechar} />;
    case 'config-grupo':
      return <ConfigGrupo canalId={camada.canalId} aoFechar={fechar} />;
    case 'sair-grupo':
      return <SairGrupo canalId={camada.canalId} aoFechar={fechar} />;
    case 'eventos':
      return <Eventos guildId={camada.guildId} aoFechar={fechar} />;
    case 'evento':
      return (
        <DetalheEvento
          guildId={camada.guildId}
          eventoId={camada.eventoId}
          aoFechar={fechar}
        />
      );
    case 'criar-evento':
      return (
        <CriarEvento
          guildId={camada.guildId}
          eventoId={camada.eventoId}
          aoFechar={fechar}
        />
      );
    case 'config-thread':
      return <ConfigThread threadId={camada.threadId} aoFechar={fechar} />;
    case 'pedir-notificacoes':
      return <PedirNotificacoes aoFechar={fechar} />;
    case 'troca-rapida':
      return <TrocaRapida aoFechar={fechar} />;
    case 'atalhos':
      return <ListaAtalhos aoFechar={fechar} />;
    case 'config-usuario':
      return <ConfigUsuario secao={camada.secao} aoFechar={fechar} />;
    case 'config-canal':
      return (
        <ConfigCanal
          canalId={camada.canalId}
          secao={camada.secao}
          aoFechar={fechar}
        />
      );
    case 'criar-canal':
      return (
        <CriarCanal
          guildId={camada.guildId}
          categoriaId={camada.categoriaId}
          aoFechar={fechar}
        />
      );
    case 'criar-categoria':
      return <CriarCategoria guildId={camada.guildId} aoFechar={fechar} />;
    case 'convidar':
      return (
        <Convidar
          guildId={camada.guildId}
          canalId={camada.canalId}
          aoFechar={fechar}
        />
      );
    case 'moderar':
      return (
        <Moderar
          acao={camada.acao}
          guildId={camada.guildId}
          userId={camada.userId}
          aoFechar={fechar}
        />
      );
    case 'criar-servidor':
      return <CriarServidor aoFechar={fechar} />;
    case 'entrar-servidor':
      return <EntrarServidor codigo={camada.codigo} aoFechar={fechar} />;
    case 'boas-vindas':
      return (
        <TelaDeBoasVindas
          guildId={camada.guildId}
          regrasDepois={camada.regrasDepois}
          aoFechar={fechar}
        />
      );
    case 'regras':
      return <AceitarRegras guildId={camada.guildId} aoFechar={fechar} />;
    case 'notificacoes-servidor':
      return (
        <NotificacoesServidor guildId={camada.guildId} aoFechar={fechar} />
      );
    case 'sair-servidor':
      return <SairServidor guildId={camada.guildId} aoFechar={fechar} />;
    case 'config-servidor':
      return (
        <ConfigServidor
          guildId={camada.guildId}
          secao={camada.secao}
          alvo={camada.alvo}
          aoFechar={fechar}
        />
      );
  }
}

export function Camadas() {
  const pilha = useCamadas((estado) => estado.pilha);
  return (
    <Suspense fallback={null}>
      {pilha.map((camada) => (
        <CamadaAberta key={camada.chave}>
          {desenhar(camada, () => fecharCamada(camada.chave))}
        </CamadaAberta>
      ))}
    </Suspense>
  );
}

function CamadaAberta({ children }: { children: ReactNode }) {
  return <>{children}</>;
}
